import { getImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
  propTypesIllustration,
  propTypesLink,
  propTypesTextHtml,
} from '../../../../constants/Proptypes';

import Button, { ButtonAppearance } from '../../../../../ui/Atoms/Button';
import Picture from '../../../../../ui/Atoms/Picture';
import Text, { TextAppearance } from '../../../../../ui/Atoms/Text';

import { getStaticUrl } from '../../../../utils/getStaticUrl';
import { hexToRGB } from '../../../../utils/hexToRGB';
import { addTrackingParamsToUrl } from '../../../../utils/trackingParams';

import * as S from './TabContent.styles';

const Tab = ({ tab, index, currentTab }) => {
  const {
    interactive_horizontal_color: color,
    interactive_horizontal_cta_datatrack: dataTrackAttribute,
    interactive_horizontal_cta_label: label,
    interactive_horizontal_cta_link: { target, url } = {},
    interactive_horizontal_description: { html: content } = {},
    interactive_horizontal_illustration: { alt, localFile } = {},
    interactive_horizontal_title_description: title,
  } = tab || '';

  const imageProps = {
    imageObject: getImage(localFile),
    imageUrl: getStaticUrl(localFile?.publicURL),
  };

  const colorRGB = hexToRGB(color, 0.5);

  const [link, setLink] = useState(url);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLink(addTrackingParamsToUrl(url));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handleCookieChange = () => {
      setLink(addTrackingParamsToUrl(url));
    };

    document.addEventListener('cookiechange', handleCookieChange);

    return () => {
      document.removeEventListener('cookiechange', handleCookieChange);
    };
  }, []);

  return (
    <S.ContentContainer isVisible={currentTab === index}>
      <S.Content>
        <S.ContentTitle>{title}</S.ContentTitle>
        {content && (
          <S.TextWrapper colorRGB={colorRGB} customColor={color}>
            <Text type={TextAppearance.NORMAL}>{content}</Text>
          </S.TextWrapper>
        )}
        {label && link && (
          <Button
            appearance={ButtonAppearance.OUTLINE}
            dataTrackAttribute={dataTrackAttribute}
            link={link}
            target={target}
          >
            {label}
          </Button>
        )}
      </S.Content>
      <S.IllustrationWrapper>
        <Picture altTract={alt} radius='30px' {...imageProps} />
      </S.IllustrationWrapper>
    </S.ContentContainer>
  );
};

export const TabContent = ({ tabs, currentTab }) => {
  if (!tabs) {
    return null;
  }

  return (
    <>
      {tabs.map((tab, index) => (
        <Tab
          key={`interactive-horizontal-tab-${index}`}
          tab={tab}
          index={index}
          currentTab={currentTab}
        />
      ))}
    </>
  );
};

TabContent.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      interactive_horizontal_color: PropTypes.string,
      interactive_horizontal_cta_datatrack: PropTypes.string,
      interactive_horizontal_cta_label: PropTypes.string,
      interactive_horizontal_cta_link: PropTypes.shape(propTypesLink),
      interactive_horizontal_description: PropTypes.shape(propTypesTextHtml),
      interactive_horizontal_illustration: PropTypes.shape(
        propTypesIllustration
      ),
      interactive_horizontal_title_description: PropTypes.string,
    }).isRequired
  ),
  currentTab: PropTypes.number,
};
